import { Injectable } from '@angular/core';

import { filter, Subscription } from 'rxjs';
import moment from 'moment-timezone';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { StackedBarChartOptionsModel } from '../../Charts/models/stackedBarChart/stacked-bar-chart-options.model';
import { CategoryValueXYChartOptions, StackTypesEnum } from '../../Charts/models/barChart/category-value-x-y-chart-options.model';
import { MapChartOptionsModel } from '../../Charts/models/map-chart.options.model';
import { PieChartOptionsModel } from '../../Charts/models/pieChart/pie-chart-options.model';
import { DateAndValueXYChartOptionsModel } from '../../Charts/models/date-and-value-x-y-chart-options.model';
import { DateAndValueXYChartUnitEnum } from '../../Charts/enums/date-and-value-x-y-chart-unit.enum';
import { PreferencesModel } from '../../../Core/Users/models/preferences.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { ChartDisplayElementModel } from '../models/chart-display-element.model';
import { HeatChartOptionsModel } from '../../Charts/models/heatChart/heat-chart-options.model';
import { ExternalChartMetricsEnum } from '../enums/external-chart-metrics.enum';
import { LabelDisplayOptionsEnum } from '../../Misc/enums/label-display-options.enum';
import { ChartStylesModel } from '../../../Common/Charts/models/chart-styles.model';
import { chartColors } from '../../Charts/enums/chart-colors.enum';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { ChartMetricsEnum } from '../enums/chart-metrics.enum';
import { SocialSeriesMetricsEnum } from '../../../Social/Intelligence/enums/social-series-metrics.enum';
import { ChartDisplaySettingsModel } from '../../Charts/models/chart-display-settings.model';
import { AbstractChartOptionsModel } from '../../Charts/models/abstract-chart-options.model';
import { WordCloudChartOptionsModel } from '../../Charts/models/WordCloudChart/word-cloud-chart-options.model';
import { chartMetricToSocialMetricAnalysisTypesMap, SocialMetricAnalysisTypesEnum } from '../../Charts/enums/social-analysis-types.enum';
import { UserCurrency } from '../../../Core/Users/enums/user-currency';
import { DataListChartOptionsModel } from '../../Charts/models/data-list-chart-options.model';
import { userAppPreferencesDefault } from '../../../Core/Users/data/user-app-preferences-default';
import { MapChartChoroplethInterpolationTypeEnum, MapChartColorSchemeEnum } from '../../Charts/models/map-chart.model';
import { ChartTimeIntervalsEnum } from '../enums/chart-time-intervals.enum';
import { ChartSpecificTypesEnum } from '../enums/chart-specific-types.enum';
import { MediaTypeEnum } from '../../Basic/enums/media-type.enum';
import { ReportBlockTypeEnum } from '../enums/report-block-type.enum';
import { Am5ChartExportLabelService } from './am5-chart-export-label.service';


@Injectable()
export class ReportsChartOptionsService {
    barChartOptions: CategoryValueXYChartOptions;
    dateAndValueXYChartOptions: DateAndValueXYChartOptionsModel;
    coverageTrendChartOptions: DateAndValueXYChartOptionsModel;
    sentimentTrendChartOptions: DateAndValueXYChartOptionsModel;
    recentTopicChartOptions: DateAndValueXYChartOptionsModel;
    mapChartOptions: MapChartOptionsModel;
    pieChartOptions: PieChartOptionsModel;
    aveOverTimeChartOptions: DateAndValueXYChartOptionsModel;
    audienceReachOverTimeChartOptions: DateAndValueXYChartOptionsModel;
    prominenceOverTimeChartOptions: DateAndValueXYChartOptionsModel;
    prominenceByReachChartOptions: HeatChartOptionsModel;
    sentimentBreakDownComparisonOptions: CategoryValueXYChartOptions;
    mediaTypeBreakDownComparisonOptions: CategoryValueXYChartOptions;
    referralSourcesOptions: CategoryValueXYChartOptions;
    socialMediaSentimentTrendChartOptions: DateAndValueXYChartOptionsModel;
    subscriptions: Subscription[] = [];
    preferences: PreferencesModel;
    currencyCode: UserCurrency;
    dataListChartOptions: DataListChartOptionsModel;
    iconBarChartOptions: CategoryValueXYChartOptions;

    sentimentChartExportLabels: any;
    mediaTypeChartExportLabels: any;

    constructor(
        private localizationService: LocalizationService,
        private currentUserStateService: CurrentUserStateService,
        private am5ChartExportLabelService: Am5ChartExportLabelService
    ) {
        this.setBarChartOptions();
        this.setMapChartOptions();
        this.setPieChartOptions();
        this.setDateAndValueXYChartOptions();
        this.setAveOverTimeChartOptions();
        this.setAudienceReachOverTimeChartOptions();
        this.setProminenceOverTimeChartOptions();
        this.setProminenceByReachChartOptions();
        this.setChartExportLabels();
        this.setMediaTypeBreakDownComparisonOptions();
        this.setSentimentBreakDownComparisonOptions();
        this.setCoverageTrendChartOptions();
        this.setRecentTopicChartOptions();
        this.setReferralSourcesOptions();
        this.setSentimentTrendChartOptions();
        this.setIconBarChartOptions();
        this.setDataListChartOptions();

        this.subscriptions.push(
            this.currentUserStateService
                .getPreferences()
                .pipe(filter(x => !!x))
                .subscribe(
                    preferences => {
                        this.preferences = preferences;
                        this.setDateAndValueXYChartOptions();
                        this.setCoverageTrendChartOptions();
                        this.setRecentTopicChartOptions();
                    }
                ),
            this.currentUserStateService.getUserCurrency().subscribe(currency => this.currencyCode = currency)
        );
    }

    setBarChartOptions(): void {
        this.barChartOptions = {
            label: this.localizationService.transform('Reports.label'),
            xValueLabel: this.localizationService.transform('Reports.count'),
            percentLabel: this.localizationService.transform('Reports.percent'),
        };
    }

    getBarChartOptions(displayElement?: ChartDisplayElementModel): CategoryValueXYChartOptions {
        const options = this.generateOptions(this.barChartOptions);

        if (displayElement) {
            options.styles = displayElement.styles;
            displayElement.enableDataDisplaySettings = true;
        }
        options.hidePreviousPeriod = !displayElement?.chartHeaderSettings?.previousPeriod;
        options.trendIndicators = displayElement?.chartHeaderSettings?.trendIndicators;
        options.chartSpecificType = displayElement.chartSpecificType;

        return options;
    }

    getTop10ChartOptions(displayElement: ChartDisplayElementModel): CategoryValueXYChartOptions {
        const top10ChartOptions: CategoryValueXYChartOptions = this.getBarChartOptions(displayElement);
        const xValueLabel: string = this.getLabelByMetric(displayElement.series[0].metric, displayElement);

        top10ChartOptions.xValueLabel = xValueLabel;
        top10ChartOptions.categoryLabelStackedOnTop = true;
        top10ChartOptions.columnHeight = 40;
        top10ChartOptions.hideLegend = true;
        top10ChartOptions.showFewerItemsAtLimitedSpace = true;
        if (!top10ChartOptions.styles) {
            top10ChartOptions.styles = new ChartStylesModel();
        }
        top10ChartOptions.styles['columnHeightThreshold'] = 8;

        return top10ChartOptions;
    }

    getOriginalPieChartBarColumnVisualizationOptions(
        displayElement: ChartDisplayElementModel,
        blockType: ReportBlockTypeEnum,
        calculatedChartSpecificType?: ChartSpecificTypesEnum
    ): CategoryValueXYChartOptions {
        const options: CategoryValueXYChartOptions = this.getBarChartOptions(displayElement);
        const chartSpecificType = calculatedChartSpecificType || displayElement.chartSpecificType;
        switch (chartSpecificType) {
            case ChartSpecificTypesEnum.BAR:
                if (displayElement.chartHeaderSettings?.previousPeriod) {
                    options.showGrid = true;
                    options.styles['columnHeightThreshold'] = 8;
                } else {
                    options.styles['columnHeightThreshold'] = 13;
                }
                options.hideLegend = true;
                options.categoryLabelStackedOnTop = true;
                options.dataPointDistinctColor = true;
                options.reverseBarSeriesOrder = !displayElement.chartHeaderSettings?.previousPeriod;
                options.reverseSourceOrder = displayElement.chartHeaderSettings?.previousPeriod;
                options.valueAxisTitle = this.getLabelByMetric(displayElement.series[0].metric, displayElement);
                options.showFewerItemsAtLimitedSpace = true;
                if (blockType === ReportBlockTypeEnum.SENTIMENT_BREAKDOWN) {
                    options.smallChartDisplayLimit = 4;
                }
                break;
            case ChartSpecificTypesEnum.MULTI_STACKED_BAR:
                options.categoryLabelStackedOnTop = true;
                options.stacked = true;
                options.stackType = StackTypesEnum.percent;
                options.hideCategoryLabel =
                    ((blockType === ReportBlockTypeEnum.SHARE_OF_VOICE
                        || blockType === ReportBlockTypeEnum.COVERAGE_COMPARISON)
                        && !displayElement.chartHeaderSettings?.previousPeriod)
                    || ((blockType === ReportBlockTypeEnum.SENTIMENT_BREAKDOWN || blockType === ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN)
                        && !displayElement.chartHeaderSettings.displaySourceAsLink && !displayElement.chartHeaderSettings.sourceLink);
                options.reverseBarSeriesOrder = true;
                break;
            case ChartSpecificTypesEnum.COLUMN:
                options.hideLegend = true;
                options.dataPointDistinctColor = true;
                break;
            case ChartSpecificTypesEnum.MULTI_STACKED_COLUMN:
                options.stacked = true;
                options.stackType = StackTypesEnum.percent;
                options.hideCategoryLabel =
                    (blockType === ReportBlockTypeEnum.SHARE_OF_VOICE
                    || blockType === ReportBlockTypeEnum.COVERAGE_COMPARISON)
                    && !displayElement.chartHeaderSettings?.previousPeriod;
                break;
        }

        this.setOriginalPieChartBarColumnExportLabels(options, displayElement, blockType);

        return options;
    }

    setOriginalPieChartBarColumnExportLabels(
        options: any,
        displayElement: ChartDisplayElementModel,
        blockType: ReportBlockTypeEnum
    ): void {
        switch (blockType) {
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
                options.exportLabels = this.sentimentChartExportLabels;
                break;
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
                options.exportLabels = this.mediaTypeChartExportLabels;
                break;
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
            case ReportBlockTypeEnum.COVERAGE_COMPARISON:
            case ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK:
                if (
                    displayElement.chartSpecificType === ChartSpecificTypesEnum.BAR
                    || displayElement.chartSpecificType === ChartSpecificTypesEnum.COLUMN
                ) {
                    options.xValueLabel = this.localizationService.transform('Reports.export.currentPeriod.count');
                    options.percentLabel = this.localizationService.transform('Reports.export.currentPeriod.percent');
                    options.previousPeriodCountExportLabel= this.localizationService.transform('Reports.export.previousPeriod.count');
                    options.previousPeriodPercentExportLanbel = this.localizationService.transform('Reports.export.previousPeriod.percent');

                    return;
                }
                if (blockType === ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK) {
                    options.exportLabels = this.mediaTypeChartExportLabels;
                } else {
                    const exportLabels = {
                        label: this.localizationService.transform('Reports.label')
                    };
                    displayElement.series.forEach(s => {
                        exportLabels[s.label] = {
                            countLabel: this.localizationService.transform(
                                'Reports.export.sourceName.count',
                                { sourceName: s.label }
                            ),
                            percentLabel: this.localizationService.transform(
                                'Reports.export.sourceName.count',
                                { sourceName: s.label }
                            )
                        };
                    });
                    options.exportLabels = exportLabels;
                }
        }
    }

    setMapChartOptions(): void {
        this.mapChartOptions = {
            label: this.localizationService.transform('Reports.label'),
            percentLabel: this.localizationService.transform('Reports.percent'),
            otherText: this.localizationService.transform('Common.other')
        };
    }

    getMapChartOptions(displayElement?: ChartDisplayElementModel): MapChartOptionsModel {
        const colorScheme = displayElement?.mapChartColorScheme || MapChartColorSchemeEnum.CHOROPLETH;

        return {
            label: this.mapChartOptions.label,
            percentLabel: this.mapChartOptions.percentLabel,
            xValueLabel: this.getLabelByMetric(displayElement.series[0].metric, displayElement),
            otherText: this.localizationService.transform('Common.other'),
            colorScheme: colorScheme,
            interpolationType: displayElement?.mapChartChoroplethInterpolationType || MapChartChoroplethInterpolationTypeEnum.LINEAR,
            styles: displayElement?.styles || undefined,
            chartSpecificType: displayElement?.chartSpecificType,
            choroplethLegendLabel: colorScheme === MapChartColorSchemeEnum.CHOROPLETH ?
                this.localizationService.transform(`Reports.formField.analysis.${displayElement?.metric || ChartMetricsEnum.volumeOfMentions}`) : ''
        };
    }

    setPieChartOptions(): void {
        this.pieChartOptions = {
            label: this.localizationService.transform('Reports.label'),
            percentLabel: this.localizationService.transform('Reports.percent'),
            xValueLabel: this.localizationService.transform('Reports.count'),
            otherLabel: this.localizationService.transform('Common.other'),
            currentPeriodLabel: this.localizationService.transform('Reports.series.currentPeriod'),
            previousPeriodLabel: this.localizationService.transform('Reports.series.previousPeriod')
        };
    }

    getPieChartOptions(displayElement?: ChartDisplayElementModel): PieChartOptionsModel {
        const options = this.generateOptions(this.pieChartOptions);

        if (displayElement) {
            options.styles = displayElement.styles;
        }

        options.radius = 85;
        if (displayElement.chartHeaderSettings?.previousPeriod) {
            options.innerRadius = 10;
        } else {
            options.innerRadius = 45;
        }

        options.chartSpecificType = ChartSpecificTypesEnum.PIE;
        options.legendPosition = displayElement.legendPosition;

        return options;
    }

    setDateAndValueXYChartOptions(): void {
        this.dateAndValueXYChartOptions = {
            label: this.localizationService.transform('Reports.label'),
            yValueLabel: this.localizationService.transform('Reports.count'),
            percentLabel: this.localizationService.transform('Reports.percent'),
            startTimeLabel: this.localizationService.transform('Reports.startTime'),
            endTimeLabel: this.localizationService.transform('Reports.endTime'),
        };

        if (this.preferences && this.preferences.timeZone) {
            this.dateAndValueXYChartOptions.timeZoneOffsetMinutes = moment().tz(this.preferences.timeZone).utcOffset();
        }
    }

    getDateAndValueXYChartOptions(unit?: DateAndValueXYChartUnitEnum, displayElement?: ChartDisplayElementModel): DateAndValueXYChartOptionsModel {
        const options = this.generateOptions(this.dateAndValueXYChartOptions);

        options.unit = unit;

        if (displayElement) {
            options.styles = displayElement.styles;
        }

        return options;
    }

    setCoverageTrendChartOptions(): void {
        this.coverageTrendChartOptions = {
            label: this.localizationService.transform('Reports.label'),
            yValueLabel: this.localizationService.transform('Reports.count'),
            percentLabel: this.localizationService.transform('Reports.percent'),
            startTimeLabel: this.localizationService.transform('Reports.startTime'),
            endTimeLabel: this.localizationService.transform('Reports.endTime'),
        };

        if (this.preferences && this.preferences.timeZone) {
            this.coverageTrendChartOptions.timeZoneOffsetMinutes = moment().tz(this.preferences.timeZone).utcOffset();
        }
    }

    getCoverageTrendChartOptions(
        unit?: DateAndValueXYChartUnitEnum,
        displayElement?: ChartDisplayElementModel,
        selectedMetrics?: ChartMetricsEnum[]
    ): DateAndValueXYChartOptionsModel {
        const options = this.generateOptions(this.coverageTrendChartOptions);

        options.unit = unit;
        options.seriesDefinitions = [];
        if (displayElement) {
            options.styles = displayElement.styles;
            switch (displayElement.timeInterval) {
                case ChartTimeIntervalsEnum.WEEK:
                    options.groupIntervals = [{ timeUnit: 'week', count: 1 }];
                    break;
                case ChartTimeIntervalsEnum.MONTH:
                    options.groupIntervals = [{ timeUnit: 'month', count: 1 }];
                    break;
                case ChartTimeIntervalsEnum.QUARTER:
                    options.groupIntervals = [{ timeUnit: 'month', count: 3 }];
                    break;
                case ChartTimeIntervalsEnum.YEAR:
                    options.groupIntervals = [{ timeUnit: 'year', count: 1 }];
                    break;
            }
        }

        if (selectedMetrics?.length > 0) {
            selectedMetrics.forEach(metric => {
                if (metric === ChartMetricsEnum.volumeOfMentions) {
                    options.seriesDefinitions.push({
                        key: ChartMetricsEnum.volumeOfMentions,
                        label: this.localizationService.transform('Reports.analysis.type.mentions')
                    });
                }
                if (metric === ChartMetricsEnum.ave ) {
                    const currency = displayElement?.styles?.currency ?? this.preferences.currency;
                    options.seriesDefinitions.push({
                        key: ChartMetricsEnum.ave,
                        label: this.localizationService.transform('Reports.analysis.type.ave') + `(${currency})`
                    });
                }
                if (metric === ChartMetricsEnum.IMPRESSIONS) {
                    options.seriesDefinitions.push({
                        key: ChartMetricsEnum.IMPRESSIONS,
                        label: this.localizationService.transform('Reports.analysis.type.audienceReach')
                    });
                }
                if (metric === ChartMetricsEnum.ENGAGEMENTS) {
                    options.seriesDefinitions.push({
                        key: ChartMetricsEnum.ENGAGEMENTS,
                        label: this.localizationService.transform(`Reports.analysis.type.${ChartMetricsEnum.ENGAGEMENTS}`)
                    });
                }
                if (metric === ChartMetricsEnum.ENGAGEMENT_RATE) {
                    options.seriesDefinitions.push({
                        key: ChartMetricsEnum.ENGAGEMENT_RATE,
                        label: this.localizationService.transform(`Reports.analysis.type.${ChartMetricsEnum.ENGAGEMENT_RATE}`)
                    });
                }
                if (metric === ChartMetricsEnum.APPLAUSE) {
                    options.seriesDefinitions.push({
                        key: ChartMetricsEnum.APPLAUSE,
                        label: this.localizationService.transform(`Reports.analysis.type.${ChartMetricsEnum.APPLAUSE}`)
                    });
                }
                if (metric === ChartMetricsEnum.CONVERSATION) {
                    options.seriesDefinitions.push({
                        key: ChartMetricsEnum.CONVERSATION,
                        label: this.localizationService.transform(`Reports.analysis.type.${ChartMetricsEnum.CONVERSATION}`)
                    });
                }
                if (metric === ChartMetricsEnum.AMPLIFICATION) {
                    options.seriesDefinitions.push({
                        key: ChartMetricsEnum.AMPLIFICATION,
                        label: this.localizationService.transform(`Reports.analysis.type.${ChartMetricsEnum.AMPLIFICATION}`)
                    });
                }
                if (metric === ChartMetricsEnum.VIEWS) {
                    options.seriesDefinitions.push({
                        key: ChartMetricsEnum.VIEWS,
                        label: this.localizationService.transform(`Reports.analysis.type.${ChartMetricsEnum.VIEWS}`)
                    });
                }
            });
        }
        if (displayElement) {
            options.events = displayElement.prevents;
        }

        options.chartSpecificType = displayElement.chartSpecificType;
        options.valueAxisRanges = options.horizontalLines;

        return options;
    }

    setRecentTopicChartOptions(): void {
        this.recentTopicChartOptions = {
            label: this.localizationService.transform('Reports.label'),
            yValueLabel: this.localizationService.transform('Reports.count'),
            yAxisTitleText: this.localizationService.transform('Reports.mentions'),
            startTimeLabel: this.localizationService.transform('Reports.startTime'),
            endTimeLabel: this.localizationService.transform('Reports.endTime'),
            unit: DateAndValueXYChartUnitEnum.NUMBER,
        };

        if (this.preferences && this.preferences.timeZone) {
            this.recentTopicChartOptions.timeZoneOffsetMinutes = moment().tz(this.preferences.timeZone).utcOffset();
        }
    }

    getRecentTopicChartOptions(displayElement?: ChartDisplayElementModel): DateAndValueXYChartOptionsModel {
        const options = this.generateOptions(this.recentTopicChartOptions);

        // need to set the lengend max item width to 140 or else the legend messes up when shrinked (goes all into 1 column, messing up the height)
        options.maxItemWidth = 140;

        options.styles = displayElement?.styles ?? new ChartStylesModel();

        options.styles.spacingUnderDate = true;

        return options;
    }

    setAveOverTimeChartOptions(): void {
        this.aveOverTimeChartOptions = {
            unit: DateAndValueXYChartUnitEnum.CURRENCY,
            label: this.localizationService.transform('Reports.label'),
            yValueLabel: this.localizationService.transform('Reports.adValue'),
            percentLabel: this.localizationService.transform('Reports.percent'),
            startTimeLabel: this.localizationService.transform('Reports.startTime'),
            endTimeLabel: this.localizationService.transform('Reports.endTime')
        };
    }

    getAveOverTimeChartOptions(displayElement?: ChartDisplayElementModel): DateAndValueXYChartOptionsModel {
        const options = this.generateOptions(this.aveOverTimeChartOptions);

        if (displayElement) {
            options.styles = displayElement.styles;
        }

        return options;
    }

    setAudienceReachOverTimeChartOptions(): void {
        this.audienceReachOverTimeChartOptions = {
            unit: DateAndValueXYChartUnitEnum.NUMBER,
            label: this.localizationService.transform('Reports.label'),
            yValueLabel: this.localizationService.transform('Reports.audienceReach'),
            yAxisTitleText: this.localizationService.transform('Reports.audienceReach'),
            percentLabel: this.localizationService.transform('Reports.percent'),
            startTimeLabel: this.localizationService.transform('Reports.startTime'),
            endTimeLabel: this.localizationService.transform('Reports.endTime')
        };
    }

    getAudienceReachOverTimeChartOptions(unit?: DateAndValueXYChartUnitEnum, displayElement?: ChartDisplayElementModel): DateAndValueXYChartOptionsModel {
        const options = this.generateOptions(this.audienceReachOverTimeChartOptions);

        options.unit = unit;

        if (displayElement) {
            options.styles = displayElement.styles;
        }

        return options;
    }

    /* eslint-disable @typescript-eslint/naming-convention */
    setProminenceOverTimeChartOptions(): void {
        this.prominenceOverTimeChartOptions = {
            unit: DateAndValueXYChartUnitEnum.PROMINENCE_LEVELS,
            label: this.localizationService.transform('Reports.label'),
            yAxisTitleText: this.localizationService.transform('Mention.details.prominence.average'),
            percentLabel: this.localizationService.transform('Reports.percent'),
            startTimeLabel: this.localizationService.transform('Reports.startTime'),
            endTimeLabel: this.localizationService.transform('Reports.endTime'),
            mentionsLabel: this.localizationService.transform('Reports.mentions'),
            averageProminenceLabel: this.localizationService.transform('Mention.details.prominence.average'),
            displayLegend: true,
            prominenceLabels : {
                0 : this.localizationService.transform('Mention.details.prominenceLevel.none'),
                1 : this.localizationService.transform('Mention.details.prominenceLevel.veryLow'),
                2 : this.localizationService.transform('Mention.details.prominenceLevel.low'),
                3 : this.localizationService.transform('Mention.details.prominenceLevel.moderate'),
                4 : this.localizationService.transform('Mention.details.prominenceLevel.high'),
                5 : this.localizationService.transform('Mention.details.prominenceLevel.veryHigh'),
            }
        };
    }

    getProminenceOverTimeChartOptions(displayElement?: ChartDisplayElementModel): DateAndValueXYChartOptionsModel {
        const options = this.generateOptions(this.prominenceOverTimeChartOptions);

        if (displayElement) {
            options.styles = displayElement.styles;
        }
        options.chartSpecificType = ChartSpecificTypesEnum.FILLED_LINE ;
        return options;
    }

    setProminenceByReachChartOptions(): void {
        this.prominenceByReachChartOptions = {
            label: this.localizationService.transform('Reports.label'),
            xLabel: this.localizationService.transform('Reports.outletProminence'),
            yLabel: this.localizationService.transform('Reports.mentionsProminence'),
            header: true,
            showLabel: true,
            showPercentage: true,
            showCount: true,
            additionalExportingLabel: this.localizationService.transform('Reports.prominenceByReach.additionalExportingLabel')
        };
    }

    getProminencByReachOptions(displayElement?: ChartDisplayElementModel): HeatChartOptionsModel {
        const options = this.generateOptions(this.prominenceByReachChartOptions);

        if (displayElement) {
            options.styles = displayElement.styles;
            options.showPercentage = displayElement.labelDisplayOptions?.includes(LabelDisplayOptionsEnum.percent);
            options.showCount = displayElement.labelDisplayOptions?.includes(LabelDisplayOptionsEnum.count);
        }

        return options;
    }



    setSentimentBreakDownComparisonOptions(): void {
        this.sentimentBreakDownComparisonOptions = {} as CategoryValueXYChartOptions;
        this.sentimentBreakDownComparisonOptions.exportLabels = this.sentimentChartExportLabels;
    }

    getSentimentBreakDownComparisonOptions(displayElement?: ChartDisplayElementModel): CategoryValueXYChartOptions {
        const options = this.generateOptions(this.sentimentBreakDownComparisonOptions);

        if (displayElement) {
            options.styles = displayElement.styles;
            options.chartSpecificType = displayElement.chartSpecificType;
            options.stacked = displayElement.chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_COLUMN
                || displayElement.chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_BAR;
        }
        options.stackType = StackTypesEnum.percent;
        options.categoryLabelStackedOnTop = true;
        options.reverseBarSeriesOrder = true;
        options.hideLegend = false;

        return options;
    }

    setSentimentTrendChartOptions(): void {
        const chartStyles = new ChartStylesModel();
        chartStyles.colorMap = {
            'POSITIVE': chartColors.agprPositive,
            'NEUTRAL': chartColors.agprNeutral,
            'NOT_AVAILABLE': chartColors.agprNotAvailable,
            'NEGATIVE': chartColors.agprNegative
        };
        chartStyles.chartHeight = 400;
        chartStyles.titleFontSize = 18;
        chartStyles.headerMarginBottom = 30;
        this.socialMediaSentimentTrendChartOptions = {
            styles: chartStyles
        };
    }

    getSentimentTrendOptions(
        displayElement: ChartDisplayElementModel,
        chosenSentiments: SearchCriteriaQuerySentimentsEnum[]
    ): DateAndValueXYChartOptionsModel {
        const options = this.generateOptions(this.socialMediaSentimentTrendChartOptions);

        if (displayElement) {
            if (displayElement.styles) {
                options.styles = displayElement.styles;
            }
            options.timeInterval = displayElement.timeInterval;
        }

        options.exportLabelDefinitions = new Map<string, string>([
            ['POSITIVE', this.localizationService.transform('Reports.sentiment.positive.count')],
            ['NEGATIVE', this.localizationService.transform('Reports.sentiment.negative.count')],
            ['NEUTRAL', this.localizationService.transform('Reports.sentiment.neutral.count')],
            ['NOT_AVAILABLE', this.localizationService.transform('Reports.sentiment.notavailable.count')],
            ['POSITIVEPercent', this.localizationService.transform('Reports.sentiment.positive.percent')],
            ['NEGATIVEPercent', this.localizationService.transform('Reports.sentiment.negative.percent')],
            ['NEUTRALPercent', this.localizationService.transform('Reports.sentiment.neutral.percent')],
            ['NOT_AVAILABLEPercent', this.localizationService.transform('Reports.sentiment.notavailable.percent')]
        ]);
        options.chartSpecificType = ChartSpecificTypesEnum.MULTI_LINE;
        options.isStacked = true;
        options.hiddenBullets = true;
        options.squareLegend = true;
        options.seriesDefinitions = [];
        options.useAmchartCursor = true;

        switch (displayElement.timeInterval) {
            case ChartTimeIntervalsEnum.WEEK:
                options.groupIntervals = [{ timeUnit: 'week', count: 1 }];
                break;
            case ChartTimeIntervalsEnum.MONTH:
                options.groupIntervals = [{ timeUnit: 'month', count: 1 }];
                break;
            case ChartTimeIntervalsEnum.QUARTER:
                options.groupIntervals = [{ timeUnit: 'month', count: 3 }];
                break;
            case ChartTimeIntervalsEnum.YEAR:
                options.groupIntervals = [{ timeUnit: 'year', count: 1 }];
                break;
        }

        if (chosenSentiments.includes(SearchCriteriaQuerySentimentsEnum.negative)) {
            options.seriesDefinitions.push({
                key: SearchCriteriaQuerySentimentsEnum.negative,
                label: this.localizationService.transform('Reports.sentiment.negative')
            });
        }
        if (chosenSentiments.includes(SearchCriteriaQuerySentimentsEnum.notAvailable)) {
            options.seriesDefinitions.push({
                key: SearchCriteriaQuerySentimentsEnum.notAvailable,
                label: this.localizationService.transform('Reports.sentiment.notAvailable')
            });
        }
        if (chosenSentiments.includes(SearchCriteriaQuerySentimentsEnum.neutral)) {
            options.seriesDefinitions.push({
                key: SearchCriteriaQuerySentimentsEnum.neutral,
                label: this.localizationService.transform('Reports.sentiment.neutral')
            });
        }
        if (chosenSentiments.includes(SearchCriteriaQuerySentimentsEnum.positive)) {
            options.seriesDefinitions.push({
                key: SearchCriteriaQuerySentimentsEnum.positive,
                label: this.localizationService.transform('Reports.sentiment.positive')
            });
        }

        return options;
    }

    setMediaTypeBreakDownComparisonOptions(): void {
        this.mediaTypeBreakDownComparisonOptions = {} as CategoryValueXYChartOptions;
        this.mediaTypeBreakDownComparisonOptions.exportLabels = this.mediaTypeChartExportLabels;
    }

    setReferralSourcesOptions(): void {
        const chartStyles = new ChartStylesModel();
        chartStyles.chartHeight = 510;
        this.referralSourcesOptions = {
            xValueLabel: '',
            percentLabel: '',
            exportLabels: {
                label: this.localizationService.transform('Reports.label'),
                'SESSIONS': {
                    countLabel: this.localizationService.transform('Reports.referralMetric.sessions.count'),
                    percentLabel: this.localizationService.transform('Reports.referralMetric.sessions.percent')
                },
                'SESSIONS_NEW': {
                    countLabel: this.localizationService.transform('Reports.referralMetric.sessionsNew.count'),
                    percentLabel: this.localizationService.transform('Reports.referralMetric.sessionsNew.percent')
                },
                'SESSIONS_RETURNING': {
                    countLabel: this.localizationService.transform('Reports.referralMetric.sessionsReturning.count'),
                    percentLabel: this.localizationService.transform('Reports.referralMetric.sessionsReturning.percent')
                },
                'USERS': {
                    countLabel: this.localizationService.transform('Reports.referralMetric.users.count'),
                    percentLabel: this.localizationService.transform('Reports.referralMetric.users.percent'),
                },
                'USERS_NEW': {
                    countLabel: this.localizationService.transform('Reports.referralMetric.usersNew.count'),
                    percentLabel: this.localizationService.transform('Reports.referralMetric.usersNew.percent')
                },
                'USERS_RETURNING': {
                    countLabel: this.localizationService.transform('Reports.referralMetric.usersReturning.count'),
                    percentLabel: this.localizationService.transform('Reports.referralMetric.usersReturning.percent')
                }
            },
            styles: chartStyles
        };
    }

    getMediaTypeBreakDownComparisonOptions(displayElement?: ChartDisplayElementModel): CategoryValueXYChartOptions {
        const options = this.generateOptions(this.mediaTypeBreakDownComparisonOptions);

        if (displayElement) {
            options.styles = displayElement.styles;
            options.chartSpecificType = displayElement.chartSpecificType;
            options.stacked = displayElement.chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_COLUMN
                || displayElement.chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_BAR;
        }
        options.stackType = StackTypesEnum.percent;
        options.categoryLabelStackedOnTop = true;
        options.reverseBarSeriesOrder = true;
        options.hideLegend = false;

        return options;
    }

    getReferralSourcesOptions(
        displayElement: ChartDisplayElementModel,
        blockType: ReportBlockTypeEnum,
        chartSpecificType?: ChartSpecificTypesEnum
    ): CategoryValueXYChartOptions {
        const options = this.getOriginalPieChartBarColumnVisualizationOptions(
            displayElement,
            blockType,
            chartSpecificType
        );
        if (displayElement?.styles) {
            options.styles = displayElement.styles;
            options.styles.chartHeight = this.referralSourcesOptions.styles.chartHeight;
        }
        options.dataPointDistinctColor = false;
        options.includeScrollbarX = true;
        options.valueAxisTitle = null;
        options.reverseBarSeriesOrder = false; // todo: this will be true when we allow clustered bars
        options.stackType = StackTypesEnum.count;
        options.includeTotalSeries = true;
        options.exportLabels = this.referralSourcesOptions.exportLabels;
        options.styles['legendItemWidth'] = 150;
        options.styles['legendItemPaddingLeft'] = 20;
        options.styles['legendContentAlign'] = 'center';

        return options;
    }

    getTopTermsOptions(displayElement?: ChartDisplayElementModel): WordCloudChartOptionsModel {
        const socialMetricAnalysisType = chartMetricToSocialMetricAnalysisTypesMap.get(displayElement.metric) ?? SocialMetricAnalysisTypesEnum.volume;
        const currencyCode = socialMetricAnalysisType === SocialMetricAnalysisTypesEnum.ave ? this.currencyCode : null;

        return {
            styles: displayElement?.styles,
            numOfItems: displayElement?.numOfItems,
            label: this.localizationService.transform('Reports.term.exporting.termLabel'),
            termValueLabel: this.localizationService.transform('Reports.term.exporting.termValueLabel'),
            tooltipDescription1: this.localizationService.transform(`Common.charts.topTerms.tooltipLabel.part1.${socialMetricAnalysisType}`),
            tooltipDescription2: this.localizationService.transform(`Common.charts.topTerms.tooltipLabel.part2.${socialMetricAnalysisType}`),
            viewMentionString: this.localizationService.transform('Reports.chart.segmentCoverageAnalysisLabel'),
            currencyCode
        };
    }

    getCoverageImpactTrendOptions(displayElement?: ChartDisplayElementModel): DateAndValueXYChartOptionsModel {
        return {
            label: this.localizationService.transform('Reports.label'),
            yAxisTitleText: this.localizationService.transform('Reports.axis.mentions'),
            yValueLabel: this.localizationService.transform('Reports.axis.mentions.value'),
            percentLabel: this.localizationService.transform('Reports.percent'),
            startTimeLabel: this.localizationService.transform('Reports.startTime'),
            endTimeLabel: this.localizationService.transform('Reports.endTime'),
            styles: displayElement && displayElement.styles,
            events: displayElement && displayElement.prevents,
            eventTypeLabel: this.localizationService.transform('Reports.eventType'),
            eventNameLabel: this.localizationService.transform('Reports.eventName'),
            eventDateLabel: this.localizationService.transform('Reports.eventDate'),
            unit: DateAndValueXYChartUnitEnum.NUMBER,
            chartSpecificType: displayElement?.chartSpecificType || ChartSpecificTypesEnum.MULTI_LINE
        };
    }

    getReferralTrendOptions(displayElement?: ChartDisplayElementModel): DateAndValueXYChartOptionsModel {
        return {
            label: this.localizationService.transform('Reports.label'),
            yAxisTitleText: this.localizationService.transform('Reports.axis.referrals'),
            yValueLabel: this.localizationService.transform('Reports.axis.referrals.value'),
            percentLabel: this.localizationService.transform('Reports.percent'),
            startTimeLabel: this.localizationService.transform('Reports.startTime'),
            endTimeLabel: this.localizationService.transform('Reports.endTime'),
            styles: displayElement && displayElement.styles,
            events: displayElement && displayElement.prevents,
            eventTypeLabel: this.localizationService.transform('Reports.eventType'),
            eventNameLabel: this.localizationService.transform('Reports.eventName'),
            eventDateLabel: this.localizationService.transform('Reports.eventDate'),
            unit: DateAndValueXYChartUnitEnum.NUMBER,
            chartSpecificType: displayElement?.chartSpecificType || ChartSpecificTypesEnum.MULTI_LINE
        };
    }

    setChartExportLabels(): void {
        this.sentimentChartExportLabels = {
            label: this.localizationService.transform('Reports.label'),
            'POSITIVE': {
                countLabel: this.localizationService.transform('Reports.sentiment.positive.count'),
                percentLabel: this.localizationService.transform('Reports.sentiment.positive.percent')
            },
            'NEGATIVE': {
                countLabel: this.localizationService.transform('Reports.sentiment.negative.count'),
                percentLabel: this.localizationService.transform('Reports.sentiment.negative.percent')
            },
            'NEUTRAL': {
                countLabel: this.localizationService.transform('Reports.sentiment.neutral.count'),
                percentLabel: this.localizationService.transform('Reports.sentiment.neutral.percent')
            },
            'NOT_AVAILABLE': {
                countLabel: this.localizationService.transform('Reports.sentiment.notavailable.count'),
                percentLabel: this.localizationService.transform('Reports.sentiment.notavailable.percent')
            }
        };

        this.mediaTypeChartExportLabels = {
            label: this.localizationService.transform('Reports.label'),
            [MediaTypeEnum.ONLINE_NEWS]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.ONLINE_NEWS}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.ONLINE_NEWS}.percent`)
            },
            [MediaTypeEnum.PRINT]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.PRINT}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.PRINT}.percent`)
            },
            [MediaTypeEnum.BLOGS]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.BLOGS}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.BLOGS}.percent`)
            },
            [MediaTypeEnum.PRESS_RELEASE]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.PRESS_RELEASE}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.PRESS_RELEASE}.percent`)
            },
            [MediaTypeEnum.TV]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.TV}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.TV}.percent`)
            },
            [MediaTypeEnum.RADIO]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.RADIO}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.RADIO}.percent`)
            },
            [MediaTypeEnum.FACEBOOK]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.FACEBOOK}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.FACEBOOK}.percent`)
            },
            [MediaTypeEnum.TWITTER]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.TWITTER}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.TWITTER}.percent`)
            },
            [MediaTypeEnum.INSTAGRAM]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.INSTAGRAM}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.INSTAGRAM}.percent`)
            },
            [MediaTypeEnum.YOUTUBE]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.YOUTUBE}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.YOUTUBE}.percent`)
            },
            [MediaTypeEnum.REDDIT]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.REDDIT}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.REDDIT}.percent`)
            },
            [MediaTypeEnum.TIKTOK]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.TIKTOK}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.TIKTOK}.percent`)
            },
            [MediaTypeEnum.MAGAZINE]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.MAGAZINE}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.MAGAZINE}.percent`)
            },
            [MediaTypeEnum.PODCAST]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.PODCAST}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.PODCAST}.percent`)
            },
            [MediaTypeEnum.SNAPCHAT]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.SNAPCHAT}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.SNAPCHAT}.percent`)
            },
            [MediaTypeEnum.THREADS]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.THREADS}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.THREADS}.percent`)
            },
            [MediaTypeEnum.LINKEDIN]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.LINKEDIN}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.LINKEDIN}.percent`)
            },
            [MediaTypeEnum.BLUESKY]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.BLUESKY}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.BLUESKY}.percent`)
            },
            [MediaTypeEnum.WECHAT]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.WECHAT}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.WECHAT}.percent`)
            },
            [MediaTypeEnum.SINA_WEIBO]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.SINA_WEIBO}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.SINA_WEIBO}.percent`)
            },
            [MediaTypeEnum.TWITCH]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.TWITCH}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.TWITCH}.percent`)
            },
            [MediaTypeEnum.PINTEREST]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.PINTEREST}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.PINTEREST}.percent`)
            },
            [MediaTypeEnum.TUMBLR]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.TUMBLR}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.TUMBLR}.percent`)
            },
            [MediaTypeEnum.TELEGRAM]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.TELEGRAM}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.TELEGRAM}.percent`)
            },
            [MediaTypeEnum.WHATSAPP]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.WHATSAPP}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.WHATSAPP}.percent`)
            },
            [MediaTypeEnum.VK]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.VK}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.VK}.percent`)
            },
            [MediaTypeEnum.OTHER]: {
                countLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.OTHER}.count`),
                percentLabel: this.localizationService.transform(`Reports.mediaType.${MediaTypeEnum.OTHER}.percent`)
            },
        };
    }

    public getLabelByMetric(metric: ChartMetricsEnum, displayElement: ChartDisplayElementModel): string {
        return this.am5ChartExportLabelService.getLabelByMetric(metric, displayElement);
    }
    setIconBarChartOptions(): void {
        this.iconBarChartOptions = {
            label: this.localizationService.transform('Reports.label'),
            xValueLabel: this.localizationService.transform('Reports.count'),
            percentLabel: this.localizationService.transform('Reports.percent'),
            imageAsCategory: false,
            hidePreviousPeriod: true,
            barDisplayLimit: 8,
            currentPeriodLabel: this.localizationService.transform('Reports.series.currentPeriod'),
            previousPeriodLabel: this.localizationService.transform('Reports.series.previousPeriod'),
        };
    }

    private generateOptions<T extends AbstractChartOptionsModel>(options: T): T {
        const res = JSON.parse(JSON.stringify(options));
        if (res.styles?.chartHeaderSettings) {
            res.styles.chartHeaderSettings = new ChartDisplaySettingsModel(options.styles.chartDisplaySettings);
        }

        return res;
    }

    setDataListChartOptions(): void {
        this.dataListChartOptions = {
            positiveTooltip: this.localizationService.transform('Reports.chart.bubbleTooltip.positive'),
            negativeTooltip: this.localizationService.transform('Reports.chart.bubbleTooltip.negative'),
            neutralTooltip: this.localizationService.transform('Reports.chart.bubbleTooltip.neutral'),
            defaultColors: userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors,
            previousPeriodLabel: this.localizationService.transform('Reports.series.previousPeriod'),
            currencyCode: UserCurrency.USD
        };
    }

    getDataListChartOptions(): DataListChartOptionsModel {
        return this.dataListChartOptions;
    }

    getDateFormat(): string {
        return this.preferences.getDateFormatString(true, true, true);
    }
}
